import {
  Box,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import {
  IStyles,
  IconWrapper,
  ImageWrapper,
  PrimaryButton,
  RemoteNumber,
  SquareImageAvatar,
  StepsBadge,
  cloneObject,
  deserify,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { HeaderWithProgressBar } from '../../../components/HeaderWithProgressBar';
import { IconDataWithLabel } from '../../../components/IconDataWithLabel';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  IClickerAssessmentInfo,
  resetClickerAssessmentState,
  setAssessmentContent,
  setClickerAssessmentInfo,
  setClickerAssessmentSessionData,
  setQuestionIndex,
  setStudentList,
  setTeacherAssessmentSessionDetails,
} from './reducer/clickerAssessment.slice';
import {
  CCSessionEnum,
  SessionModeEnum,
  SessionStatusEnum,
  TaskEnum,
  TeacherAssessmentAttemptModeEnum,
} from '@protos/learning_management/lms.db_pb';
import {
  getClickerDisconnectReason,
  isBaseStationEffectivelyOnline,
  useClickerAssessmentContext,
} from '../../../app/Context/ClickerAssessmentContextProvider';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  CLICKER_ASSESSMENT,
  HOME,
} from '../../../routeHandling/RoutesNomenclature';
import { useNavigate } from 'react-router-dom';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { Question } from '@protos/content_management/content.db_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { TeacherPreviousAssessmentSession } from '@protos/learning_management/lms.clickerAssessment.teacher.apis_pb';
import { buildSortedHomeworkContent } from 'apps/teacher/src/utils/functions';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(20), md: pxTovW(20), lg: pxTovW(240) },
    paddingBottom: { xs: pxToRem(80), md: pxTovW(40) },
  },
  topBanner: {
    display: 'flex',
    gap: { xs: pxToRem(20), md: pxTovW(7) },
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #007CDC',
    backgroundColor: '#F5FAFF',
    paddingY: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(20) },
    marginX: { xs: pxToRem(0), md: pxTovW(20), lg: pxTovW(240) },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: { xs: pxToRem(10), md: pxTovW(15) },
  },
  startButton: {
    width: { xs: '90%', md: pxTovW(500), lg: pxTovW(444) },
    position: { xs: 'fixed', md: 'unset' },
    bottom: { xs: 10, md: 'unset' },
    zIndex: 10,
  },

  bodyBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: { xs: 'unset', md: 'space-between' },
    mt: { xs: pxToRem(20), md: pxTovW(40) },
  },
  leftPanel: {
    width: { xs: '100%', md: pxTovW(800), lg: pxTovW(541) },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(22), md: pxTovW(35) },
  },
  connectedClassStats: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: { xs: pxToRem(10), md: pxTovW(22) },
    boxShadow: `0px 0px ${pxTovW(10)} #00000029`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
  },
  menuTriggerHeading: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(20), md: pxTovW(22) },
    boxShadow: `0px 0px ${pxTovW(10)} #00000029`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    p: { xs: pxToRem(10), md: pxTovW(22) },
    cursor: 'pointer',
  },
  menu: {
    '& .MuiMenu-list': {
      padding: { xs: pxToRem(10), md: pxTovW(10) },
      boxShadow: `0 0 ${pxTovW(10)} #00000038`,
      boxSizing: 'border-box',
    },
    '& .MuiPaper-root': {
      borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    },
  },
  menuItem: {
    p: { md: `${pxTovW(20)} ${pxTovW(15)}` },
    backgroundColor: '#FFFFFF',
    '&.MuiMenuItem-root': { backgroundColor: '#FFFFFF' },
    '&.Mui-focusVisible': { backgroundColor: '#FFFFFF' },
    display: 'flex',
    flexDirection: 'column',
  },
  insideMenuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(20), md: pxTovW(22) },
    mb: { xs: pxToRem(10), md: pxTovW(0) },
  },
  studentList: {
    boxShadow: `0px 0px ${pxTovW(10)} #00000029`,
    borderRadius: { xs: pxToRem(15), md: pxTovW(10) },
    paddingX: { xs: pxToRem(14), md: pxTovW(25) },
    maxHeight: { xs: pxToRem(400), md: pxTovW(750), lg: pxTovW(500) },
    overflowY: 'auto',
  },
  studentListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingY: { xs: pxToRem(14), md: pxTovW(25) },
  },

  rightPanel: {
    width: { xs: '100%', md: pxTovW(900), lg: pxTovW(750) },
    boxSizing: 'border-box',
  },
  clickerImageBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(22) },
    mt: { xs: pxToRem(25), md: pxTovW(40) },
  },
  clickerImage: {
    width: { xs: '100%', md: pxTovW(200), lg: pxTovW(195) },
  },
};

export const ClickerAssessmentStart = () => {
  const { setSelectedFunction, isIpadOnly, setSelectedLogoClickHandler } =
    useGlobalContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const {
    LmsConnectedClassAPIServiceV1WithClientStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const {
    upsertConnectedClassSessionApiv2,
    upsertConnectedClassSessionApiV3,
    unsubscribeFromFirestore,
    caBaseStationData,
  } = useClickerAssessmentContext();
  const { current_assessmemt_classStats, selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const {
    student_list,
    ca_base_station_details,
    clicker_assessment_info,
    assessment_content,
    session_start_type,
  } = deserify(useAppSelector((state) => state.clickerAssessment));
  const teacher_id = getLocalStorage('userId');
  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [studentListLoading, setStudentListLoading] = useState<
    'loading' | 'error' | 'success'
  >('success');

  // ------------------------------------------------------------

  const startTestCTA = async () => {
    startTeacherClickerSession();
  };

  // ------------------------------------------------------------

  const getPreviousAssessmentSessionInfo = async (
    assessmentContent: AssessmentContent
  ) => {
    try {
      const response =
        await LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler.getPreviousAssessmentSessionInfo(
          {
            teacherId: BigInt(teacher_id),
            assessmentId: selected_assessment_id,
          }
        );
      if (response.data) {
        const newClickerAssessmentInfo: IClickerAssessmentInfo = {
          ...clicker_assessment_info,
          previous_session_info: response.data,
        };
        dispatch(
          setClickerAssessmentInfo(cloneObject(newClickerAssessmentInfo))
        );
        const newIndex = getNextQuestionIndex(response.data, assessmentContent);
        if (newIndex === -1) {
          dispatch(setQuestionIndex(0));
        } else {
          dispatch(setQuestionIndex(newIndex));
        }
      } else {
        //
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  // ------------------------------------------------------------

  const startTeacherClickerSession = async () => {
    try {
      setLoading('loading');
      const sessionData = await upsertConnectedClassSessionApiV3({
        classId: current_assessmemt_classStats?.assessment?.classId,
        sectionId: current_assessmemt_classStats?.assessment?.sectionId,
        // subject: current_assessmemt_classStats?.assessment?.subject,
        baseReceiverId: ca_base_station_details?.baseReceiverId,
        teacherId: teacher_id,
        referenceId: selected_assessment_id,
        // lessonId: lesson_id,
        // schoolId: current_assessmemt_classStats?.assessment.sch,
        // teacherLessonSessionId: Number(lesson_session_id),
        sessionStatus: SessionStatusEnum.SESSION_STATUS_STARTED,
        sessionType: CCSessionEnum.CC_SESSION_TYPE_ASSESSMENT,
      });
      const clickerSession =
        await LmsClickerAssessmentTeacherAPIServiceV1ClientWithStatusCodeHandler.createTeacherAssessmentSession(
          {
            teacherId: teacher_id,
            assessmentId:
              current_assessmemt_classStats?.assessment?.assessmentId,
            sessionStatus: clicker_assessment_info?.selected_attempt
              ? clicker_assessment_info.selected_attempt.attemptStatus
              : SessionStatusEnum.SESSION_STATUS_STARTED,
            sessionMode: SessionModeEnum.SESSION_MODE_CLICKER_ASSESSMENT,
            connectedClassSessionId: sessionData?.connectedClassSessionId,
            teacherAssessmentAttemptMode:
              getTestActionLabel(
                session_start_type,
                clicker_assessment_info
              ).toLowerCase() === 'start'
                ? TeacherAssessmentAttemptModeEnum.TEACHER_ASSESSMENT_ATTEMPT_MODE_FIRST_START
                : TeacherAssessmentAttemptModeEnum.TEACHER_ASSESSMENT_ATTEMPT_MODE_RESUME,
          }
        );
      if (clickerSession && sessionData) {
        dispatch(setTeacherAssessmentSessionDetails(clickerSession));
        dispatch(setClickerAssessmentSessionData(sessionData));
        navigate(CLICKER_ASSESSMENT);
      } else {
        dispatch(
          setToastInfo({
            label: 'Teacher Assessment creation failed',
            variant: 'error',
            open: true,
          })
        );
      }
      setLoading('success');
    } catch (err) {
      console.log(err);
      setLoading('error');
      dispatch(
        setToastInfo({
          label: 'Teacher Assessment creation failed',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ------------------------------------------------------------

  const backButtonClickHanlder = (navigateType: 'back' | 'home') => {
    // unsubscribeFromFirestore();
    dispatch(resetClickerAssessmentState());
    if (navigateType === 'home') {
      navigate(HOME);
    } else {
      navigate(-1);
    }
  };

  // ------------------------------------------------------------

  const getStudentListAndAssessmentContent = async () => {
    try {
      setStudentListLoading('loading');
      const response =
        await LmsConnectedClassAPIServiceV1WithClientStatusCodeHandler.getClassStudentList(
          {
            teacherId: teacher_id,
            classId: current_assessmemt_classStats?.assessment?.classId,
            sectionId: current_assessmemt_classStats?.assessment?.sectionId,
          }
        );
      const response2 =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacher_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId:
              current_assessmemt_classStats?.assessment?.assessmentId,
          }
        );
      if (
        response.status === 200 &&
        response2.status === 200 &&
        response2.data
      ) {
        const list = response.studentListData?.studentClickerData || [];
        dispatch(setStudentList(list));
        const hw = response2.data;
        const taskQuestionInfo =
          hw.questionsSequenceInfo?.taskQuestionInfo || [];
        // const sortedQuestionIds = taskQuestionInfo
        //   .sort((a, b) => a.sequence - b.sequence)
        //   .map((val) => val.questionId)
        //   .filter((val) => val !== undefined) as string[];
        // const sortedHwArray = sortedQuestionIds
        //   .map((id) =>
        //     hw.assessmentContent.find((elem) => elem.questionId === id)
        //   )
        //   .filter((elem) => elem !== undefined) as Question[];
        // const updatedHw = cloneObject(hw);
        // updatedHw.assessmentContent = sortedHwArray;
        const updatedHw = buildSortedHomeworkContent(hw);
        dispatch(setAssessmentContent(updatedHw));
        // dispatch(setQuestionIndex(0));
        if (session_start_type === 'start') {
          dispatch(setQuestionIndex(0));
        } else {
          await getPreviousAssessmentSessionInfo(updatedHw);
        }
        setStudentListLoading('success');
      } else {
        dispatch(setStudentList(undefined));
        dispatch(setAssessmentContent(undefined));
        dispatch(setQuestionIndex(0));
        setStudentListLoading('error');
        return;
      }
    } catch (err) {
      console.log(err);
      setStudentListLoading('error');
    }
  };

  // ------------------------------------------------------------

  // const clickerAssessmentSessionCreation = async (
  //   sessionStatus: SessionStatusEnum
  // ) => {
  // const sessionData = await upsertConnectedClassSessionApiv2({
  //   classId: current_assessmemt_classStats?.assessment?.classId,
  //   sectionId: current_assessmemt_classStats?.assessment?.sectionId,
  //   // subject: current_assessmemt_classStats?.assessment?.subject,
  //   baseReceiverId: ca_base_station_details?.baseReceiverId,
  //   teacherId: teacher_id,
  //   // lessonId: lesson_id,
  //   // schoolId: current_assessmemt_classStats?.assessment.sch,
  //   // teacherLessonSessionId: Number(lesson_session_id),
  //   sessionStatus: sessionStatus,
  //   connectedClassSessionId:
  //     sessionStatus === SessionStatusEnum.SESSION_STATUS_EXITED ||
  //     sessionStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
  //       ? clicker_assessment_session_data?.connectedClassSessionId
  //       : undefined,
  // });

  //   if (sessionStatus === SessionStatusEnum.SESSION_STATUS_EXITED) {
  //     return;
  //   }
  //   if (sessionData) {
  //     dispatch(setClickerAssessmentSessionData(sessionData));
  //   } else {
  //     dispatch(
  //       setToastInfo({
  //         label: 'upsertConnectedClassSessionApi err',
  //         variant: 'error',
  //         open: true,
  //       })
  //     );
  //     return;
  //   }
  // };

  const getOnlineKeyPadsCount = () => {
    // Count against the class roster, not the raw keyPads list — the Firestore
    // doc can hold stale entries from old mappings which inflate the counts
    const keyPads = caBaseStationData?.keyPads || [];
    const students = student_list || [];
    const onlineKeyPadsCount = students.filter((student) =>
      keyPads.some(
        (keypad) =>
          keypad.keypadSn === student.clickerSerialNo && keypad.isOnline
      )
    ).length;

    return `${onlineKeyPadsCount}/${students.length}`;
  };

  // ------------------------------------------------------------

  const getSecondaryTitle = () => {
    const className = current_assessmemt_classStats?.assessment?.class;
    const sectionName = current_assessmemt_classStats?.assessment?.section;
    const subjectName = current_assessmemt_classStats?.assessment?.subject;
    if (
      current_assessmemt_classStats?.assessment?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM ||
      current_assessmemt_classStats?.assessment?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_AUTOMATED
    ) {
      const uniqueSubjects = new Set(
        current_assessmemt_classStats?.assessment?.subjects
      );
      const uniqueSubjectsArray = Array.from(uniqueSubjects).join(', ');
      return `${className} ${sectionName} | ${uniqueSubjectsArray}`;
    } else {
      return `${subjectName} | ${className} ${sectionName}`;
    }
  };

  // ------------------------------------------------------------

  useEffect(() => {
    setSelectedFunction(() => () => backButtonClickHanlder('back'));
    setSelectedLogoClickHandler(() => () => backButtonClickHanlder('home'));
    if (teacher_id) {
      getStudentListAndAssessmentContent();
    }
    // getPreviousAssessmentSessionInfo();
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [session_start_type]);
  // useEffect(() => {
  //   getPreviousAssessmentSessionInfo();
  // }, [assessment_content?.assessment?.assessmentId]);

  // ------------------------------------------------------------

  const checkOnlineStatus = (clickerSerialNo: string) => {
    const keypad = caBaseStationData?.keyPads?.find(
      (kp: any) => kp.keypadSn === clickerSerialNo
    );
    return keypad?.isOnline ? 'on' : 'off';
  };

  const isEffectivelyOnline = isBaseStationEffectivelyOnline(
    caBaseStationData,
    current_assessmemt_classStats?.assessment?.classId,
    current_assessmemt_classStats?.assessment?.sectionId
  );

  const disconnectReason = getClickerDisconnectReason({
    baseStation: caBaseStationData,
    classId: current_assessmemt_classStats?.assessment?.classId,
    sectionId: current_assessmemt_classStats?.assessment?.sectionId,
  });

  return (
    <Box>
      <Box sx={styles.topBanner}>
        <IconWrapper
          name={isEffectivelyOnline ? 'wifi' : 'no_connection'}
          size={isIpadOnly ? 'large' : 'md'}
          parentFolder="icons"
          type="png"
        />
        {isEffectivelyOnline ? (
          <Typography variant="h2">Connected Class</Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h2">Disconnected</Typography>
            {disconnectReason ? (
              <Tooltip title={disconnectReason} arrow>
                <IconButton
                  size="small"
                  aria-label={disconnectReason}
                  sx={{ color: '#007CDC' }}
                >
                  <InfoTwoToneIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        )}
      </Box>

      <Box sx={styles.root}>
        <Box sx={styles.headerContainer}>
          <Box
            sx={{ width: { xs: '100%', md: pxTovW(1000), lg: pxTovW(602) } }}
          >
            <HeaderWithProgressBar
              imagePath={getMediaBasePath(
                assessment_content?.assessment?.assessmentPosterImgUrl,
                'processedMediaBucket'
              )}
              primaryTitle={
                current_assessmemt_classStats?.assessment?.assessmentTitle
              }
              secondaryTitle={getSecondaryTitle()}
            />
          </Box>

          <PrimaryButton
            onClick={startTestCTA}
            sx={styles.startButton}
            disabled={
              !isEffectivelyOnline ||
              !ca_base_station_details?.isOnline ||
              loading === 'loading'
            }
            color={
              getTestActionLabel(
                session_start_type,
                clicker_assessment_info
              ).toLowerCase() === 'start'
                ? 'secondary'
                : 'primary'
            }
          >
            <Typography variant="h3" color="common.white" fontWeight="bold">
              {getTestActionLabel(
                session_start_type,
                clicker_assessment_info
              ).toLowerCase() === 'start'
                ? 'START TEST'
                : 'RESUME'}
            </Typography>
          </PrimaryButton>
        </Box>

        <Box sx={styles.bodyBox}>
          <Box sx={styles.leftPanel}>
            {studentListLoading === 'loading' ? (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: { xs: pxToRem(71), md: pxTovW(120) },
                  borderRadius: { xs: pxToRem(10) },
                }}
              />
            ) : (
              <Box sx={styles.connectedClassStats}>
                <IconDataWithLabel
                  iconName="user-plus"
                  data={getOnlineKeyPadsCount()}
                  label="Joined"
                />
                <IconDataWithLabel
                  iconName="user2"
                  data={student_list?.length.toString() || ''}
                  label="Total"
                />
                <IconDataWithLabel
                  iconName={'science2'}
                  data={`${current_assessmemt_classStats?.assessment?.class} ${current_assessmemt_classStats?.assessment?.section}`}
                  label={`${
                    current_assessmemt_classStats?.assessment?.subject || ' '
                  }`}
                  hideDivider
                />
              </Box>
            )}

            <Box onClick={(e) => setAnchorEl(e.currentTarget)}>
              {isMobile && anchorEl === null ? (
                <Box sx={styles.menuTriggerHeading}>
                  <StepInstructionHeading />
                  <Box>
                    <KeyboardArrowDownIcon fontSize="medium" />
                  </Box>
                </Box>
              ) : null}
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              sx={styles.menu}
            >
              <MenuItem
                onClick={() => setAnchorEl(null)}
                autoFocus={false}
                disableGutters
                sx={styles.menuItem}
              >
                <Box sx={styles.insideMenuHeader}>
                  <StepInstructionHeading />
                  <Box>
                    <KeyboardArrowDownIcon fontSize="medium" />
                  </Box>
                </Box>

                <Divider orientation="horizontal" flexItem />

                <StepInstructionsCard />
              </MenuItem>
            </Menu>

            {studentListLoading === 'loading' ? (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: { xs: pxToRem(250), md: pxTovW(500) },
                  borderRadius: { xs: pxToRem(10) },
                }}
              />
            ) : (
              <Box sx={styles.studentList}>
                <Box sx={styles.studentListHeader}>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(24) } }}
                  >
                    Name
                  </Typography>

                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(24) } }}
                  >
                    Remote
                  </Typography>
                </Box>

                <Divider orientation="horizontal" flexItem />

                <Box>
                  {student_list
                    ? student_list.map((student, index) => (
                        <>
                          <StudentRemoteCard
                            key={index}
                            // imageSource={}
                            name={student.studentName}
                            remoteNumber={index + 1}
                            online={
                              checkOnlineStatus(student.clickerSerialNo) ===
                              'on'
                            }
                          />

                          {index < nameList.length - 1 && (
                            <Divider orientation="horizontal" flexItem />
                          )}
                        </>
                      ))
                    : null}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={styles.rightPanel}>
            {isMobile ? null : (
              <>
                <StepInstructionHeading />
                <Box sx={styles.clickerImageBox}>
                  <ImageWrapper
                    name="clicker_back_web"
                    type="png"
                    parentFolder="images"
                    styles={styles.clickerImage}
                  />

                  <ImageWrapper
                    name="clicker_front_web"
                    type="png"
                    parentFolder="images"
                    styles={styles.clickerImage}
                  />
                </Box>

                <StepInstructionsCard />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// -------------------------------------------------------------

interface IStudentRemoteCard {
  name: string;
  imageSource?: string;
  remoteNumber: number;
  online?: boolean;
}
const StudentRemoteCard = (props: IStudentRemoteCard) => {
  const { name, imageSource, remoteNumber, online } = props;
  return (
    <Grid
      container
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingY: { xs: pxToRem(14), md: pxTovW(25) },
      }}
    >
      <Grid item xs={1.7} md={1.5}>
        <SquareImageAvatar
          size="small"
          name={name}
          imageStyles={{
            width: { xs: pxToRem(35), md: pxTovW(60), lg: pxTovW(55) },
            height: { xs: pxToRem(35), md: pxTovW(60), lg: pxTovW(55) },
          }}
          ImageSource={imageSource}
          online={online}
        />
      </Grid>
      <Grid item xs={6.5} md={7}>
        <Typography
          variant="h2"
          fontWeight="regular"
          sx={{
            fontSize: { xs: pxToRem(16), md: pxTovW(24) },
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {name}
        </Typography>
      </Grid>
      <Grid item xs={1.7} md={1.5}>
        <RemoteNumber
          number={remoteNumber}
          size="small"
          textStyles={{ fontSize: { xs: pxToRem(20), md: pxTovW(25) } }}
          rootStyles={{
            width: { xs: pxToRem(35), md: pxTovW(60), lg: pxTovW(55) },
            height: { xs: pxToRem(35), md: pxTovW(60), lg: pxTovW(55) },
          }}
        />
      </Grid>
    </Grid>
  );
};

// -------------------------------------------------------------

const StepInstructionHeading = () => {
  return (
    <Box>
      <Typography
        variant="h1"
        textAlign="center"
        sx={{ fontSize: { xs: pxToRem(14), md: pxTovW(40) } }}
      >
        Welcome To Your Connected Class.
      </Typography>
      <Typography
        variant="h4"
        textAlign="center"
        sx={{
          fontSize: { xs: pxToRem(12), md: pxTovW(32) },
          color: '#666666',
          mt: { xs: pxToRem(10), md: pxTovW(0) },
        }}
      >
        Follow these steps for the best experience.
      </Typography>
    </Box>
  );
};

// -------------------------------------------------------------

const StepInstructionsCard = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: { xs: pxToRem(25), md: pxTovW(40) },
        gap: { xs: pxToRem(20), md: pxTovW(30) },
      }}
    >
      {STEPS_DATA.map((info, index) => (
        <Grid
          key={index}
          container
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', md: 'space-between' },
            alignItems: 'center',
            gap: { xs: pxToRem(10), md: pxTovW(15) },
          }}
        >
          <Grid item xs={12} md={2.2}>
            <StepsBadge
              number={index + 1}
              backgroundColor={info.countBackground}
              bordercolor={info.countBorderColor}
              rootStyles={{
                width: { xs: pxToRem(90), md: pxTovW(124) },
                margin: { xs: 'auto', md: 'unset' },
              }}
            />
          </Grid>
          <Grid item xs={12} md={9.5}>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                textAlign: { xs: 'center', md: 'unset' },
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                fontSize: { xs: pxToRem(14), md: pxTovW(22) },
              }}
            >
              {info.stepInstructions}
            </Typography>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};

// -------------------------------------------------------------

const nameList = [
  'Ruturaj Atre Ruturaj AtreRuturaj AtreRuturaj AtreRuturaj AtreRuturaj Atre',
  'Advitiya Sharma',
  'Srinivas Adepu',
  'Gowtham Kumar',
  'Swapnil Nikumbh',
  'Advitiya Sharma',
  'Srinivas Adepu',
  'Gowtham Kumar',
];

const STEPS_DATA = [
  {
    countBackground: '#FFF4CA',
    countBorderColor: '#F8C807',
    stepInstructions:
      'Distribute clickers to students as per the registered clicker numbers.',
  },
  {
    countBackground: '#E8F5FF',
    countBorderColor: '#007CDC',
    stepInstructions:
      'Turn ON the clicker by pressing the middle button to join the connected class.',
  },
  {
    countBackground: '#FFE4E4',
    countBorderColor: '#F54040',
    stepInstructions:
      'Check how many students have joined and start teaching when ready.',
  },
];

// -------------------------------------------------------------
function getNextQuestionIndex(
  prevSession?: TeacherPreviousAssessmentSession,
  assessmentContent?: AssessmentContent
) {
  const completedQuestionIds: string[] =
    prevSession?.completedQuestionIds || [];
  const questions = assessmentContent?.assessmentContent || [];

  if (completedQuestionIds.length === 0 || questions.length === 0) {
    return 0;
  }
  const newIndex = questions.findIndex(
    (q) => q.questionId && !completedQuestionIds.includes(q.questionId)
  );

  return newIndex === -1 ? 0 : newIndex;
}
// const shouldStartTest = (
//   startType: 'start' | 'resume',
//   clickerAssessmentInfo?: IClickerAssessmentInfo
// ): boolean => {
//   const completedQuestionIds =
//     clickerAssessmentInfo?.previous_session_info?.completedQuestionIds;
//   const hasCompletedQuestions =
//     Array.isArray(completedQuestionIds) && completedQuestionIds.length > 0;
//   const sessionStatus =
//     clickerAssessmentInfo?.previous_session_info?.sessionStatus;
//   const isSessionPaused =
//     sessionStatus === SessionStatusEnum.SESSION_STATUS_PAUSED;

//   return startType === 'start' || isSessionPaused || !hasCompletedQuestions;
// };
const getTestActionLabel = (
  startType: 'start' | 'resume',
  clickerAssessmentInfo?: IClickerAssessmentInfo
): 'Start' | 'Resume' => {
  if (startType === 'start') {
    return 'Start';
  }
  const completedQuestionIds =
    clickerAssessmentInfo?.previous_session_info?.completedQuestionIds ?? [];

  const sessionStatus =
    clickerAssessmentInfo?.previous_session_info?.sessionStatus;

  const isCompletedEmpty = completedQuestionIds.length === 0;

  if (isCompletedEmpty) {
    return sessionStatus === SessionStatusEnum.SESSION_STATUS_PAUSED
      ? 'Resume'
      : 'Start';
  }

  return 'Resume';
};

// startType === 'start' - 'Start'
// No questions completed, session is paused - 'Resume'
// No questions completed, session is not paused -	'Start'
// Some questions completed - 'Resume'
